import { useEffect, useState } from "react";
import { AppLayout } from "../components/layout/AppLayout";
import { PublicLayout } from "../components/layout/PublicLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";
import { api, ApiError } from "../lib/api";
import { openPaddleCheckout } from "../lib/paddle";
import { openLemonSqueezyCheckout } from "../lib/lemonsqueezy";
import type { PricingPlan, UserRole } from "../types";

const FALLBACK_TALENT_PLANS: PricingPlan[] = [
  {
    id: "starter",
    name: "Starter",
    price: "$0",
    audience: "talent",
    interval: null,
    features: [
      "Instant access with Google SSO",
      "Master Career Vault registration",
      "1 basic Persona Card",
      "1 free AI Chat Interview credit (15 turns)",
      "Daily rate limit: 15 turns / day",
    ],
  },
  {
    id: "pro_talent_monthly",
    name: "Pro Talent (Monthly)",
    price: "$12",
    audience: "talent",
    interval: "month",
    features: [
      "Up to 15 AI Chat Interviews / mo (300 turns)",
      "Unlimited AI Resume generation",
      "Priority Team Matchmaker exposure",
      "Personal career coaching insights",
    ],
    popular: true,
  },
  {
    id: "pro_talent_yearly",
    name: "Pro Talent (Annual)",
    price: "$99",
    audience: "talent",
    interval: "year",
    features: [
      "180 AI Chat Interviews / yr (3,600 turns)",
      "~30% savings vs monthly plan ($8.25/mo)",
      "Unlimited AI Resume generation",
      "Priority customer support",
    ],
  },
  {
    id: "interview_credit",
    name: "Interview Credit (5 Packs)",
    price: "$4.99",
    audience: "talent",
    interval: null,
    features: [
      "5 AI Chat Interview Packs (50 turns)",
      "5 Full AI Competency & Culture reports",
      "Pay-as-you-go, no monthly commitment",
    ],
  },
];

const FALLBACK_COMPANY_PLANS: PricingPlan[] = [
  {
    id: "company_trial",
    name: "Company Trial (7 Days)",
    price: "$0",
    audience: "company",
    interval: null,
    features: [
      "Team culture profile setup",
      "5 talent searches / month",
      "Basic Team-fit scores",
    ],
  },
  {
    id: "team_fit",
    name: "Team Fit",
    price: "$79",
    audience: "company",
    interval: "month",
    features: [
      "Unlimited talent search & filters",
      "Detailed Team-fit analysis",
      "10 AI interview invites / month (200 turns)",
      "Company hiring reports",
    ],
    popular: true,
  },
  {
    id: "scale_up",
    name: "Scale Up",
    price: "$199",
    audience: "company",
    interval: "month",
    features: [
      "All Team Fit plan features",
      "Up to 50 AI interviews & reports / mo",
      "API integration support",
      "Dedicated account manager",
    ],
  },
];

export default function Pricing() {
  const { user, subscription, credits, refreshSubscription } = useAuth();
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [audience, setAudience] = useState<UserRole>(user?.role ?? "talent");
  const [checkingOutId, setCheckingOutId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ plans: PricingPlan[] }>("/api/payments/pricing")
      .then((res) => {
        if (res.plans && res.plans.length > 0) {
          setPlans(res.plans);
        }
      })
      .catch(() => undefined);
  }, []);

  async function subscribe(plan: PricingPlan) {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    setCheckingOutId(plan.id);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await api.post<{ provider?: string; checkoutUrl?: string; transactionId?: string }>(
        "/api/payments/checkout",
        { planId: plan.id }
      );

      if (res.provider === "lemonsqueezy" && res.checkoutUrl) {
        await openLemonSqueezyCheckout(res.checkoutUrl);
      } else if (res.provider === "paddle" && res.transactionId) {
        await openPaddleCheckout(res.transactionId);
      } else {
        const demoRes = await api.post<{ ok: boolean; message: string }>("/api/payments/demo-grant", { planId: plan.id });
        setSuccessMsg(demoRes.message);
        await refreshSubscription();
      }
    } catch (err) {
      if (err instanceof ApiError && (err.status === 501 || err.status === 404)) {
        try {
          const demoRes = await api.post<{ ok: boolean; message: string }>("/api/payments/demo-grant", { planId: plan.id });
          setSuccessMsg(demoRes.message);
          await refreshSubscription();
        } catch (demoErr) {
          setError(demoErr instanceof Error ? demoErr.message : "Failed to process payment.");
        }
      } else {
        setError(err instanceof Error ? err.message : "Failed to create checkout session.");
      }
    } finally {
      setCheckingOutId(null);
    }
  }

  const apiPlans = plans.filter((p) => p.audience === audience);
  const fallbackPlans = audience === "talent" ? FALLBACK_TALENT_PLANS : FALLBACK_COMPANY_PLANS;
  const sourcePlans = apiPlans.length > 0 ? apiPlans : fallbackPlans;
  const filtered = sourcePlans.map((plan) => {
    const fb = fallbackPlans.find((f) => f.id === plan.id || f.name.includes(plan.name) || plan.name.includes(f.name));
    return {
      ...plan,
      features: plan.features && plan.features.length > 0 ? plan.features : fb?.features ?? [],
      popular: plan.popular ?? fb?.popular ?? false,
    };
  });

  const isPaid =
    subscription &&
    (subscription.plan === "pro_talent" || subscription.plan === "team_fit" || subscription.plan === "scale_up") &&
    (subscription.status === "active" || subscription.status === "trialing");

  const Content = (
    <div className="mx-auto max-w-6xl px-4 pb-16 pt-6 sm:px-6 sm:pt-10 lg:px-8">
      {user && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <div>
            <p className="text-xs font-semibold text-slate-400">YOUR CURRENT PLAN & BALANCE</p>
            <p className="mt-1 text-sm font-bold text-navy">
              {isPaid ? `✨ ${subscription?.plan.toUpperCase()} Plan (Active)` : "Free Plan"}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-teal/10 px-3 py-1 text-xs font-bold text-teal-dark">
              💳 Chat Credits: {credits?.interview_credits ?? 0}
            </span>
            <span className="rounded-full bg-navy/10 px-3 py-1 text-xs font-bold text-navy">
              💳 Report Credits: {credits?.report_credits ?? 0}
            </span>
          </div>
        </div>
      )}

      <div className="text-center">
        <h1 className="text-2xl font-bold text-navy sm:text-3xl">Simple & Flexible Pricing</h1>
        <p className="mt-2 text-sm text-slate-500 sm:text-base">
          Choose the plan that fits your career stage or team size.
        </p>
      </div>

      <div className="mt-6 flex justify-center gap-2 sm:mt-8">
        <ToggleButton active={audience === "talent"} onClick={() => setAudience("talent")}>
          Talent
        </ToggleButton>
        <ToggleButton active={audience === "company"} onClick={() => setAudience("company")}>
          Teams
        </ToggleButton>
      </div>

      {successMsg && (
        <Card className="mx-auto mt-4 max-w-md border border-teal/30 bg-teal/5 text-center text-xs font-bold text-teal-dark">
          🎉 {successMsg}
        </Card>
      )}

      {error && (
        <Card className="mx-auto mt-4 max-w-md border border-coral/30 bg-coral/5 text-center text-xs text-coral">
          {error}
        </Card>
      )}

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((plan) => {
          const isCurrentPlan =
            subscription &&
            ((plan.id.includes("pro") && subscription.plan === "pro_talent") ||
              (plan.id === "team_fit" && subscription.plan === "team_fit") ||
              (plan.id === "scale_up" && subscription.plan === "scale_up"));

          return (
            <Card
              key={plan.id}
              className={`relative flex flex-col border transition hover:shadow-card-hover ${
                plan.popular ? "border-teal ring-1 ring-teal/20" : "border-slate-100"
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-4 rounded-full bg-teal px-3 py-0.5 text-[10px] font-semibold text-white">
                  Recommended
                </span>
              )}
              <div>
                <p className="text-sm font-semibold text-navy sm:text-base">{plan.name}</p>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-navy sm:text-3xl">{plan.price}</span>
                  {plan.interval && (
                    <span className="text-xs text-slate-400">
                      / {plan.interval === "month" ? "mo" : "yr"}
                    </span>
                  )}
                  {!plan.interval && plan.price !== "$0" && <span className="text-xs text-slate-400">/ pack</span>}
                </div>
              </div>

              <ul className="mt-4 flex-1 space-y-2">
                {plan.features?.map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-xs text-slate-600 sm:text-sm">
                    <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {feat}
                  </li>
                ))}
              </ul>

              <Button
                fullWidth
                size="sm"
                className="mt-5"
                variant={isCurrentPlan ? "outline" : plan.price === "$0" ? "outline" : "primary"}
                loading={checkingOutId === plan.id}
                disabled={isCurrentPlan || Boolean(checkingOutId)}
                onClick={() => subscribe(plan)}
              >
                {isCurrentPlan ? "Current Active Plan" : plan.price === "$0" ? "Start Free" : "Subscribe / Buy"}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );

  return user ? <AppLayout title="Pricing & Plans">{Content}</AppLayout> : <PublicLayout>{Content}</PublicLayout>;
}

function ToggleButton({
  active,
  children,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
        active ? "bg-navy text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
      }`}
    >
      {children}
    </button>
  );
}
